import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate, useLocation } from 'react-router-dom';
import { FiCheckCircle, FiAlertCircle, FiLoader, FiXCircle } from 'react-icons/fi';
import { verifyPaymentSession } from '../services/stripeService';
import { useAuth } from '../context/AuthContext';

const PaymentSuccess = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { fetchUserInfo, firebaseUser, isLoading: authLoading } = useAuth();
  const [verificationResult, setVerificationResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [paymentType, setPaymentType] = useState('unknown');

  useEffect(() => {
    const pathname = location.pathname;
    const sessionId = searchParams.get('session_id');
    const status = searchParams.get('status');
    const errorParam = searchParams.get('error');
    const message = searchParams.get('message');

    // Don't proceed if authentication is still loading
    if (authLoading) {
      console.log('PaymentSuccess: Waiting for authentication to load...');
      return;
    }

    // Determine payment type based on URL and parameters
    if (pathname === '/payment/success' && status === 'success') {
      setPaymentType('success');
      if (sessionId) {
        verifyPayment(sessionId);
      } else {
        setError('No session ID provided for successful payment');
        setLoading(false);
      }
    } else if (pathname === '/payment/cancelled') {
      setPaymentType('cancelled');
      setError(decodeURIComponent(message || 'Payment was cancelled'));
      setLoading(false);
    } else if (pathname === '/payment/error') {
      setPaymentType('error');
      const errorMessage = decodeURIComponent(errorParam || 'Payment processing failed');
      setError(errorMessage);
      setLoading(false);
    } else {
      setPaymentType('unknown');
      setError('Invalid payment page');
      setLoading(false);
    }
  }, [searchParams, location, authLoading]); // Add authLoading to dependencies

  const verifyPayment = async (sessionId) => {
    setLoading(true);
    try {
      console.log('Verifying payment session:', sessionId);
      const result = await verifyPaymentSession(sessionId);
      setVerificationResult(result);
      
      // Wait for authentication to be ready before refreshing user info
      if (!authLoading && fetchUserInfo && firebaseUser && firebaseUser.emailVerified) {
        try {
          console.log('Refreshing user info after successful payment verification');
          await fetchUserInfo(firebaseUser);
        } catch (error) {
          console.error('Failed to refresh user info after payment:', error);
          // Don't fail the entire payment verification if user info refresh fails
        }
      } else {
        console.log('Skipping user info refresh - auth not ready or user not verified:', {
          authLoading,
          hasFirebaseUser: !!firebaseUser,
          userVerified: firebaseUser?.emailVerified
        });
      }
      
      // Redirect to chat after a short delay for successful payments
      setTimeout(() => {
        navigate('/chat');
      }, 3000);
      
    } catch (error) {
      console.error('Payment verification error:', error);
      setError(error.message || 'Payment verification failed');
    } finally {
      setLoading(false);
    }
  };

  if (loading || authLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-bela-50 to-white flex items-center justify-center">
        <div className="bg-white rounded-xl shadow-lg p-8 max-w-md w-full mx-4">
          <div className="text-center">
            <FiLoader className="animate-spin text-bela-600 mx-auto mb-4" size={48} />
            <h1 className="text-2xl font-bold text-gray-900 mb-2">
              {authLoading ? 'Loading...' : 'Verifying Payment...'}
            </h1>
            <p className="text-gray-600">
              {authLoading 
                ? 'Please wait while we authenticate your session' 
                : 'Please wait while we confirm your payment'
              }</p>
          </div>
        </div>
      </div>
    );
  }

  // Handle different payment scenarios
  const renderPaymentResult = () => {
    switch (paymentType) {
      case 'success':
        if (error) {
          // Success URL but verification failed
          return (
            <div className="text-center">
              <FiAlertCircle className="text-red-500 mx-auto mb-4" size={48} />
              <h1 className="text-2xl font-bold text-gray-900 mb-2">
                Payment Verification Failed
              </h1>
              <p className="text-gray-600 mb-6">
                {error}
              </p>
              <p className="text-sm text-gray-500 mb-6">
                Your payment may have been processed, but we couldn't verify it. Please contact support if needed.
              </p>
              <button
                onClick={() => navigate('/chat')}
                className="bg-bela-600 hover:bg-bela-700 text-white font-semibold py-2 px-6 rounded-lg transition-colors"
              >
                Continue to Chat
              </button>
            </div>
          );
        }
        
        // Successful payment
        return (
          <div className="text-center">
            <FiCheckCircle className="text-green-500 mx-auto mb-4" size={48} />
            <h1 className="text-2xl font-bold text-gray-900 mb-2">
              Payment Successful!
            </h1>
            <p className="text-gray-600 mb-4">
              Your subscription is now active. Welcome to Premium!
            </p>
            
            {verificationResult && (
              <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6">
                <div className="text-sm text-green-800">
                  <p><strong>Subscription:</strong> {verificationResult.subscriptionStatus}</p>
                  {verificationResult.customer && (
                    <p><strong>Email:</strong> {verificationResult.customer.email}</p>
                  )}
                  {verificationResult.payment && (
                    <p><strong>Amount:</strong> ${(verificationResult.payment.amount / 100).toFixed(2)} {verificationResult.payment.currency.toUpperCase()}</p>
                  )}
                </div>
              </div>
            )}
            
            <p className="text-sm text-gray-500 mb-6">
              You'll be redirected to the chat in a few seconds...
            </p>
            
            <button
              onClick={() => navigate('/chat')}
              className="bg-bela-600 hover:bg-bela-700 text-white font-semibold py-2 px-6 rounded-lg transition-colors"
            >
              Continue to Chat
            </button>
          </div>
        );

      case 'cancelled':
        return (
          <div className="text-center">
            <FiXCircle className="text-orange-500 mx-auto mb-4" size={48} />
            <h1 className="text-2xl font-bold text-gray-900 mb-2">
              Payment Cancelled
            </h1>
            <p className="text-gray-600 mb-6">
              {error || 'Your payment was cancelled. No charges were made.'}
            </p>
            <div className="space-y-3">
              <button
                onClick={() => navigate('/chat')}
                className="w-full bg-bela-600 hover:bg-bela-700 text-white font-semibold py-2 px-6 rounded-lg transition-colors"
              >
                Continue to Chat
              </button>
              <button
                onClick={() => window.location.reload()}
                className="w-full bg-gray-200 hover:bg-gray-300 text-gray-800 font-semibold py-2 px-6 rounded-lg transition-colors"
              >
                Try Again
              </button>
            </div>
          </div>
        );

      case 'error':
      default:
        return (
          <div className="text-center">
            <FiAlertCircle className="text-red-500 mx-auto mb-4" size={48} />
            <h1 className="text-2xl font-bold text-gray-900 mb-2">
              Payment Error
            </h1>
            <p className="text-gray-600 mb-6">
              {error || 'An error occurred while processing your payment.'}
            </p>
            <div className="space-y-3">
              <button
                onClick={() => navigate('/chat')}
                className="w-full bg-bela-600 hover:bg-bela-700 text-white font-semibold py-2 px-6 rounded-lg transition-colors"
              >
                Continue to Chat
              </button>
              <button
                onClick={() => window.location.reload()}
                className="w-full bg-gray-200 hover:bg-gray-300 text-gray-800 font-semibold py-2 px-6 rounded-lg transition-colors"
              >
                Try Again
              </button>
            </div>
          </div>
        );
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-bela-50 to-white flex items-center justify-center">
      <div className="bg-white rounded-xl shadow-lg p-8 max-w-md w-full mx-4">
        {renderPaymentResult()}
      </div>
    </div>
  );
};

export default PaymentSuccess;
